import { find } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AdditionalOrderColumn, FilterDefinitionType, OrdersTab } from 'enums';
import { Orders } from 'features/orders';
import {
  useUserContext,
  useCurrencies,
  useOrderQueryFilters,
  useWhiteTriangle,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import { FilterDefinition, OrderFilters } from 'types';
import { requisitesUtils, formUtils } from 'utils';

export const TraderHoldCompletedOrders: React.FC = () => {
  const { t: tFilters } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders.filters',
  });

  const { paymentTypes, banks } = useUserContext();
  const { getFiatCurrencyCode, fiatCurrenciesOptions } = useCurrencies();

  const { isWhiteTriangle, requisitesId } = useWhiteTriangle();
  useOrderQueryFilters({
    ...(isWhiteTriangle && { requisitesId }),
  });

  const additionalFilters: FilterDefinition<OrderFilters>[] = useMemo(
    () => [
      {
        label: tFilters('payment_type'),
        name: 'paymentTypeId',
        type: FilterDefinitionType.Select,
        options: requisitesUtils.getPaymentTypesOptions(paymentTypes),
        hidden: isWhiteTriangle,
        getDisplayName: (value: string) =>
          requisitesUtils.getPaymentTypeLabel(
            find(paymentTypes, { id: value })!,
          ),
      },
      {
        label: tFilters('bank'),
        name: 'bankId',
        type: FilterDefinitionType.Select,
        options: formUtils.getOptions(banks),
        hidden: isWhiteTriangle,
        getDisplayName: (value: string) => find(banks, { id: value })?.name,
      },
      {
        label: tFilters('fiat_currency'),
        name: 'fiatCurrencyId',
        type: FilterDefinitionType.Select,
        options: fiatCurrenciesOptions,
        getDisplayName: getFiatCurrencyCode,
        hidden: isWhiteTriangle,
      },
      ...(!isWhiteTriangle
        ? requisitesUtils.getRequisitesFieldsFilters<OrderFilters>()
        : []),
    ],
    [
      banks,
      fiatCurrenciesOptions,
      getFiatCurrencyCode,
      paymentTypes,
      tFilters,
      isWhiteTriangle,
    ],
  );

  return (
    <Orders
      tab={OrdersTab.HoldCompleted}
      additionalFilters={additionalFilters}
      additionalColumns={[
        AdditionalOrderColumn.CustomerPayment,
        AdditionalOrderColumn.TraderCard,
        AdditionalOrderColumn.StatusActive,
      ]}
    />
  );
};
